import * as PropTypes from 'prop-types';
import * as React from 'react';
import PriceFieldWithCurrency from '../../shared/price_field_with_currency';
import Tooltip from '../../tooltip';
import UrlHelpers from '../../shared/url_helpers';
import { UserStore, APIRequest } from '@reverbdotcom/discovery-ui';
import debounce from '../../shared/debounce';
import LoadingOverlay from '../../shared/loading_overlay';
import Money from '../../shared/money';
import * as classNames from 'classnames';

const EXCHANGE_DELAY = 500; // ms

class MakeAnOffer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isFormOpen: false,
      offerAmount: '',
      exchangedOfferAmount: null,
      offerMessage: '',
      isAgreedToPay: false,
      isSubmitting: false,
      isSubmitSuccess: false,
      isSubmitError: false,
      errorMessage: null,
    };

    this.handleToggleForm = this.handleToggleForm.bind(this);
    this.handleChangeAmount = this.handleChangeAmount.bind(this);
    this.handleChangeMessage = this.handleChangeMessage.bind(this);
    this.handleCheckAgreedToPay = this.handleCheckAgreedToPay.bind(this);
    this.handleClickSubmit = this.handleClickSubmit.bind(this);
    this.handleOfferSuccess = this.handleOfferSuccess.bind(this);
    this.handleOfferError = this.handleOfferError.bind(this);
    this.handleExchangeSuccess = this.handleExchangeSuccess.bind(this);
    this.debouncedExchangeAmount = debounce(this.exchangeAmount.bind(this), EXCHANGE_DELAY);
  }

  handleToggleForm(e) {
    e.preventDefault();
    this.setState({ isFormOpen: !this.state.isFormOpen });
  }

  handleChangeAmount(e) {
    this.setState({ offerAmount: e.target.value });
    if (this.needsCurrencyExchange) { this.debouncedExchangeAmount(e); }
  }

  handleChangeMessage(e) {
    this.setState({ offerMessage: e.target.value });
  }

  handleCheckAgreedToPay(e) {
    this.setState({ isAgreedToPay: e.target.checked });
  }

  handleClickSubmit(e) {
    e.preventDefault();
    if (!this.state.isAgreedToPay) { this.refs.agreeCheckbox.focus(); }
    if (!this.canSubmit) { return; }

    this.setState({ isSubmitting: true });

    APIRequest.V3.post(
      UrlHelpers.placeOfferPath(this.props.productId),
      this.submitParams,
    )
      .then(this.handleOfferSuccess)
      .fail(this.handleOfferError);
  }

  handleOfferSuccess() {
    this.setState({
      isSubmitting: false,
      isSubmitSuccess: true,
      isSubmitError: false,
    });
  }

  handleOfferError(response) {
    this.setState({
      isSubmitting: false,
      isSubmitError: true,
      errorMessage: response.responseJSON.message,
    });
  }

  handleExchangeSuccess(response) {
    this.setState({
      exchangedOfferAmount: response.amount,
      isExchanging: false,
    });
  }

  exchangeAmount() {
    if (this.state.offerAmount.length === 0) {
      this.setState({
        exchangedOfferAmount: null,
      });
      return;
    }

    this.setState({ isExchanging: true });

    APIRequest.V3.get(UrlHelpers.inverseReverseCurrencyExchangePath({
      source: {
        currency: this.userCurrency,
        amount: this.state.offerAmount,
      },
      target_currency: this.productCurrency,
    }))
      .then(this.handleExchangeSuccess);
  }

  get submitParams() {
    return {
      price: {
        amount: this.submitAmount,
        currency: this.productCurrency,
      },
      message: this.state.offerMessage,
    };
  }

  get submitAmount() {
    if (this.needsCurrencyExchange) {
      return this.state.exchangedOfferAmount;
    }

    return this.state.offerAmount;
  }

  get productCurrency() {
    return this.props.productCurrency;
  }

  get userCurrency() {
    return UserStore.getState().currency;
  }

  get needsCurrencyExchange() {
    return this.productCurrency !== this.userCurrency;
  }

  get canSubmit() {
    if (this.state.isSubmitting) { return false; }
    if (this.needsCurrencyExchange && !this.state.exchangedOfferAmount) { return false; }
    if (!this.state.isAgreedToPay) { return false; }

    return this.state.offerAmount.length > 0;
  }

  get userNotActivated() {
    return !UserStore.getState().isActivated;
  }

  translate(key, args = {}) {
    return I18n.t(`digitalListing.makeAnOffer.${key}`, args);
  }

  renderCurrencyExchange() {
    if (!this.state.exchangedOfferAmount) { return null; }

    const tooltip = this.translate('currencyExchange.tooltip', {
      listingCurrency: this.productCurrency,
      buyerCurrency: this.userCurrency,
    });
    const exchangedAmount = Money.parse(
      this.state.exchangedOfferAmount,
      this.productCurrency,
    ).toStringWithCurrency();

    return (
      <LoadingOverlay showOverlay={this.state.isExchanging}>
        <div className="panel mb-1">
          <p className="strong mb-0">
            {this.translate('currencyExchange.yourOffer', { amount: exchangedAmount })}
          </p>
          <p className="tight-line-height mb-0">
            {this.translate('currencyExchange.conversionNote', { currency: this.userCurrency })}
            <Tooltip title={tooltip} className="ml-half" />
          </p>
        </div>
      </LoadingOverlay>
    );
  }

  renderNotActivated() {
    return (
      <div className="alert alert-info alert-small">
        <p className="small mb-half">
          {this.translate('unactivated.confirmEmail')}
        </p>
        <a
          href="/users/activations"
          className="offer-input__email-confirm-button"
        >
          {this.translate('unactivated.showMeHow')}
        </a>
      </div>
    );
  }

  renderForm() {
    if (!this.state.isFormOpen) { return null; }
    if (this.userNotActivated) { return this.renderNotActivated(); }

    return (
      <form className="offer-input">
        {this.renderError()}

        <section className="mb-1">
          <label className="pt-1">{I18n.t('js.offerInput.bestOffer')}</label>
          <PriceFieldWithCurrency
            currency={this.userCurrency}
            onChange={this.handleChangeAmount}
            required
            name="amount"
          />
        </section>

        {this.renderCurrencyExchange()}

        <label>{this.translate('message.label')}</label>
        <textarea
          onChange={this.handleChangeMessage}
          value={this.state.offerMessage}
          className="wide"
          rows="4"
          name="message"
        />
        <div className="custom-checkbox mb-1">
          <label className="custom-checkbox__label normal">
            <input
              onChange={this.handleCheckAgreedToPay}
              checked={this.state.isAgreedToPay}
              required
              type="checkbox"
              name="agree"
              ref="agreeCheckbox"
            />
            <span className="custom-checkbox__overlay" />
            <span className="custom-checkbox__text">
              {this.translate('agreeToPay.label')}
            </span>
          </label>
        </div>
        <input
          type="submit"
          className={classNames(
            'offer-input__submit-button',
            { 'offer-input__submit-button--disabled': !this.canSubmit },
          )}
          value={this.translate('submit')}
          onClick={this.handleClickSubmit}
        />
      </form>
    );
  }

  renderButton() {
    if (this.state.isFormOpen) {
      return (
        <a
          href="#"
          className="offer-input__cancel-button"
          onClick={this.handleToggleForm}
        >
          <i className="fa fa-remove" /> {this.translate('button.cancel')}
        </a>
      );
    }

    return (
      <a
        href="#"
        className="offer-input__make-button"
        onClick={this.handleToggleForm}
        data-registration="required"
        data-registration-message={this.translate('button.registrationMessage')}
      >
        {this.translate('button.initial')}
      </a>
    );
  }

  renderSuccess() {
    return (
      <div className="alert alert-success alert-small mtb-0 mlr-0 offer-submitted">
        {this.translate('success', {
          amount: Money.parse(this.state.offerAmount, this.userCurrency).toStringWithCurrency(),
        })}
      </div>
    );
  }

  renderError() {
    if (!this.state.isSubmitError) { return null; }

    return (
      <div className="alert alert-error alert-small mb-0">
        {this.state.errorMessage}
      </div>
    );
  }

  renderInitial() {
    return (
      <div>
        {this.renderButton()}
        {this.renderForm()}
      </div>
    );
  }

  renderContents() {
    if (this.state.isSubmitSuccess) {
      return this.renderSuccess();
    }

    return this.renderInitial();
  }

  render() {
    return (
      <div className="mt-1">
        {this.renderContents()}
      </div>
    );
  }
}

MakeAnOffer.propTypes = {
  productId: PropTypes.number.isRequired,
  productCurrency: PropTypes.string.isRequired,
};

export default MakeAnOffer;



// WEBPACK FOOTER //
// ./javascripts/components/digital_listings/show_page/make_an_offer.jsx